import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { BookOpen, CalendarCheck, Loader2, AlertCircle, Check } from "lucide-react";
import { BOOK_DOWNLOAD_URL, WEBINAR_LINE } from "@/config";
import { Countdown } from "@/components/Countdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("verifying");
    const [reserved, setReserved] = useState(false);
    const [reserving, setReserving] = useState(false);
    const attempts = useRef(0);

    const customer = (() => {
        try { return JSON.parse(localStorage.getItem("coa_customer")) || {}; } catch { return {}; }
    })();

    useEffect(() => {
        if (!sessionId) { setState("error"); return; }
        let cancelled = false;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    setReserved(!!data.webinar_reserved);
                    setState("paid");
                    return;
                }
            } catch { /* keep polling */ }
            attempts.current += 1;
            if (attempts.current < 30 && !cancelled) setTimeout(poll, 2000);
            else if (!cancelled) setState("error");
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    const reserveSeat = async () => {
        setReserving(true);
        try {
            await axios.post(`${API}/webinar/reserve`, { session_id: sessionId });
            setReserved(true);
        } catch { /* noop */ }
        setReserving(false);
    };

    return (
        <div data-testid="success-page" className="min-h-screen bg-[#0B0614] text-white flex items-center justify-center px-5 py-24 grain relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(168,85,247,0.2),transparent_60%)] pointer-events-none" />
            <div className="absolute -bottom-40 -right-32 w-[50vw] h-[50vw] rounded-full bg-royal/20 blur-[120px] pointer-events-none" />

            {state === "verifying" && (
                <div data-testid="success-verifying" className="relative text-center">
                    <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto mb-6" />
                    <p className="font-mono text-xs tracking-[0.3em] uppercase text-zinc-400">Confirming your payment…</p>
                </div>
            )}

            {state === "error" && (
                <div data-testid="success-error" className="relative text-center max-w-md">
                    <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-6" />
                    <h1 className="font-serif uppercase text-2xl mb-4">We couldn't confirm your payment</h1>
                    <p className="text-sm text-zinc-400 mb-8">If you completed checkout, your access email is on its way. Otherwise, please try again.</p>
                    <Link data-testid="success-back-link" to="/" className="font-mono text-xs tracking-[0.25em] uppercase text-gold border border-gold/40 px-8 py-4 hover:bg-gold hover:text-black transition-colors duration-300 inline-block">
                        Back to Checkout
                    </Link>
                </div>
            )}

            {state === "paid" && (
                <motion.div
                    initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="relative w-full max-w-3xl"
                >
                    <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-6 text-center">
                        {customer.first_name ? `Welcome, ${customer.first_name}` : "Payment Confirmed"}
                    </p>
                    <h1 data-testid="success-headline" className="font-serif uppercase font-semibold tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-[1.08] text-center mb-16">
                        Welcome to <span className="gold-text italic">Consciousness of Abundance</span>
                    </h1>

                    <div className="grid sm:grid-cols-2 gap-6">
                        <div data-testid="download-book-card" className="gold-border-beam bg-[#120A24] p-8 sm:p-10 flex flex-col">
                            <BookOpen className="w-6 h-6 text-gold mb-6" />
                            <h2 className="font-serif uppercase text-xl sm:text-2xl font-semibold mb-3">Download Your Book</h2>
                            <p className="text-sm text-zinc-400 leading-relaxed mb-8 flex-1">Your instant digital copy of Consciousness of Abundance is ready.</p>
                            <a
                                data-testid="download-book-button"
                                href={BOOK_DOWNLOAD_URL}
                                download="Consciousness-of-Abundance-Toriano-Sanzone.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-gold text-black text-center font-mono text-xs tracking-[0.2em] uppercase px-6 py-4 hover:bg-gold-light transition-colors duration-300"
                            >
                                Download Now
                            </a>
                        </div>

                        <div data-testid="webinar-seat-card" className="gold-border-beam bg-[#120A24] p-8 sm:p-10 flex flex-col">
                            <CalendarCheck className="w-6 h-6 text-gold mb-6" />
                            <h2 className="font-serif uppercase text-xl sm:text-2xl font-semibold mb-3">Join Toriano Live</h2>
                            <p className="text-sm text-zinc-400 leading-relaxed mb-8 flex-1">{WEBINAR_LINE}. Your invitation details are saved to {customer.email || "your email"}.</p>
                            {reserved ? (
                                <div data-testid="webinar-reserved-confirmation" className="flex items-center justify-center gap-2 border border-gold/40 bg-gold/10 text-gold font-mono text-xs tracking-[0.2em] uppercase px-6 py-4">
                                    <Check className="w-4 h-4" /> Seat Reserved — Tue 7 PM CT
                                </div>
                            ) : (
                                <button
                                    data-testid="reserve-webinar-button"
                                    onClick={reserveSeat}
                                    disabled={reserving}
                                    className="border border-gold/50 text-gold font-mono text-xs tracking-[0.2em] uppercase px-6 py-4 hover:bg-gold hover:text-black transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                                >
                                    {reserving && <Loader2 className="w-4 h-4 animate-spin" />}
                                    Reserve My Webinar Seat
                                </button>
                            )}
                        </div>
                    </div>

                    <p className="mt-12 text-center font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-600">
                        A receipt was sent to {customer.email || "your email"} — see you Tuesday.
                    </p>
                    <div className="mt-6 flex justify-center">
                        <Countdown compact />
                    </div>
                </motion.div>
            )}
        </div>
    );
}
